import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import {
  MagnifyingGlassIcon,
  EyeIcon,
  PencilSquareIcon,
  ClockIcon,
  DocumentCheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ArrowRightIcon,
  RocketLaunchIcon,
  UsersIcon,
  UserPlusIcon,
  ArrowsUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { useTable, usePagination } from "react-table";
import clsx from "clsx";
import PlanBadge from "../PlanBadge";
import MemberViewModal from "./MemberViewModal";
import Swal from "sweetalert2";
import Select from "react-select";
import {
  publishTrackerMemberSelection,
} from "../../utils/trackerBridge";

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
    yellow: "bg-amber-100 text-amber-700 ring-amber-200",
    orange: "bg-orange-100 text-orange-700 ring-orange-200",
    purple: "bg-purple-100 text-purple-700 ring-purple-200",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const EmptyState = ({
  title = "No results",
  subtitle = "Try adjusting your search or filters.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
    <svg
      viewBox="0 0 24 24"
      className="mb-2 h-8 w-8 text-gray-400"
      fill="none"
      stroke="currentColor"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
    <div className="text-sm font-medium text-gray-900">{title}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

const movementUpdateForMap = {
  "0": { label: "Inactivation", color: "red" },
  "1": { label: "Activation", color: "green" },
  "2": { label: "Termination/Cancellation", color: "red" },
  "3": { label: "Suspension", color: "yellow" },
  "4": { label: "Expiration", color: "slate" },
  "5": { label: "Extension", color: "blue" },
  "6": { label: "Change of Plan", color: "yellow" },
  "7": { label: "Reprint", color: "blue" },
  "8": { label: "Card Replacement", color: "blue" },
  renewal: { label: "Renewal", color: "blue" },
  change_plan: { label: "Change of Plan", color: "yellow" },
};

const getMovementUpdateForMeta = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return movementUpdateForMap[normalized] || { label: value || "N/A", color: "gray" };
};

const formatMovementDate = (value, includeTime = false) => {
  const raw = String(value ?? "").trim();
  if (!raw || raw.startsWith("0000-00-00")) {
    return "N/A";
  }

  const candidate = raw.includes("T") ? raw : raw.replace(" ", "T");
  const parsed = new Date(candidate);

  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  if (includeTime) {
    return parsed.toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  return parsed.toLocaleDateString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const MOVEMENT_HISTORY_EXPORT_COLUMNS = [
  { key: "update_for", label: "Update For" },
  { key: "effective_date", label: "Effective Date" },
  { key: "processed_by", label: "Processed By" },
  { key: "reason", label: "Reason" },
  { key: "remarks", label: "Remarks" },
  { key: "processed_date", label: "Processed Date" },
];

const HISTORY_FILTER_DEFAULTS = Object.freeze({
  search_string: "",
  update_for: "all",
  processed_by: "all",
  effective_date_from: "",
  effective_date_to: "",
  processed_date_from: "",
  processed_date_to: "",
});

const MEMBER_BATCH_ACTIONS = Object.freeze([
  {
    key: "member-batch-enrollment",
    label: "Batch Member Enrollment",
    path: "/member-batch-enrollment",
    icon: UsersIcon,
    buttonClass:
      "border-blue-600 bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-200",
  },
  {
    key: "member-batch-renewal-upload",
    label: "Batch Renewal Upload",
    path: "/member-batch-renewal-upload",
    icon: ArrowDownTrayIcon,
    buttonClass:
      "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-200",
  },
  {
    key: "member-batch-activation-upload",
    label: "Batch Activation Upload",
    path: "/member-batch-activation-upload",
    icon: UserPlusIcon,
    buttonClass:
      "border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-200",
  },
  {
    key: "member-batch-deactivation-upload",
    label: "Batch Deactivation Upload",
    path: "/member-batch-deactivation-upload",
    icon: ArrowDownTrayIcon,
    buttonClass:
      "border-rose-600 bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-200",
  },
  {
    key: "member-batch-change-plan-upload",
    label: "Batch Change Plan Upload",
    path: "/member-batch-change-plan-upload",
    icon: ArrowRightIcon,
    buttonClass:
      "border-amber-600 bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-200",
  },
  {
    key: "member-batch-text-alert-mobile-update-upload",
    label: "Batch Text Alert Mobile Update",
    path: "/member-batch-text-alert-mobile-update-upload",
    icon: RocketLaunchIcon,
    buttonClass:
      "border-cyan-600 bg-cyan-600 text-white hover:bg-cyan-700 focus:ring-cyan-200",
  },
]);

const MEMBER_SPECIFIC_FILTER_FIELDS = Object.freeze([
  { key: "member_id", label: "Member ID" },
  { key: "employee_id", label: "Employee ID" },
  { key: "last_name", label: "Last Name" },
  { key: "first_name", label: "First Name" },
  { key: "middle_name", label: "Middle Name" },
]);

const MEMBER_SPECIFIC_FILTER_DEFAULTS = Object.freeze({
  member_id: "",
  employee_id: "",
  last_name: "",
  first_name: "",
  middle_name: "",
});

const normalizeMemberSearchText = (value) => String(value ?? "").trim();

const normalizeMemberSpecificFilters = (filters = {}) =>
  MEMBER_SPECIFIC_FILTER_FIELDS.reduce((normalizedFilters, field) => {
    normalizedFilters[field.key] = normalizeMemberSearchText(filters[field.key]);
    return normalizedFilters;
  }, {});

const toOptionalFilterParam = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized === "" ? undefined : normalized;
};

const normalizeCorporateOptions = (rows = []) =>
  (rows || [])
    .map((row) => {
      const corpCode = String(row?.value ?? row?.corp_code ?? "").trim();
      if (!corpCode) return null;

      const corpName = String(
        row?.corp_name ?? row?.actual_corp_name ?? row?.label ?? ""
      ).trim();
      const label = corpName ? `${corpName} (${corpCode})` : corpCode;

      return {
        value: corpCode,
        label,
      };
    })
    .filter(Boolean);

const mergeCorporateOptions = (existingOptions = [], incomingRows = []) => {
  const mergedMap = new Map();

  normalizeCorporateOptions(existingOptions).forEach((option) => {
    mergedMap.set(option.value, option);
  });
  normalizeCorporateOptions(incomingRows).forEach((option) => {
    mergedMap.set(option.value, option);
  });

  return Array.from(mergedMap.values()).sort((leftOption, rightOption) =>
    leftOption.label.localeCompare(rightOption.label)
  );
};

const toMovementHistoryExportCellValue = (row, key) => {
  switch (key) {
    case "update_for":
      return getMovementUpdateForMeta(row.update_for).label;
    case "effective_date":
      return formatMovementDate(row.effective_date);
    case "processed_date":
      return formatMovementDate(row.processed_date, true);
    default:
      return row[key] ?? "";
  }
};

const exportMovementHistoryToCsv = (rows, fileName) => {
  const escapeCsvValue = (value) => {
    if (value == null) return "";
    const raw = String(value);
    if (/["\n,]/.test(raw)) {
      return `"${raw.replace(/"/g, '""')}"`;
    }
    return raw;
  };

  const headerLine = MOVEMENT_HISTORY_EXPORT_COLUMNS.map((column) =>
    escapeCsvValue(column.label)
  ).join(",");

  const rowLines = rows.map((row) =>
    MOVEMENT_HISTORY_EXPORT_COLUMNS.map((column) =>
      escapeCsvValue(toMovementHistoryExportCellValue(row, column.key))
    ).join(",")
  );

  const csvContent = [headerLine, ...rowLines].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const exportMovementHistoryToExcel = async (rows, fileName) => {
  const XLSX = await import("xlsx");

  const exportRows = rows.map((row) => {
    const exportRow = {};
    MOVEMENT_HISTORY_EXPORT_COLUMNS.forEach((column) => {
      exportRow[column.label] = toMovementHistoryExportCellValue(row, column.key);
    });
    return exportRow;
  });

  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Member Movement");
  XLSX.writeFile(workbook, fileName);
};

const ACTION_MENU_WIDTH = 288;
const ACTION_MENU_HEIGHT = 300;
const ACTION_MENU_MARGIN = 12;
const ACTION_MENU_GAP = 8;

const getActionMenuPosition = (triggerElement) => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = rect.right - ACTION_MENU_WIDTH;
  left = Math.max(
    ACTION_MENU_MARGIN,
    Math.min(left, viewportWidth - ACTION_MENU_WIDTH - ACTION_MENU_MARGIN)
  );

  let top = rect.bottom + ACTION_MENU_GAP;
  let placement = "bottom";

  if (top + ACTION_MENU_HEIGHT > viewportHeight - ACTION_MENU_MARGIN) {
    top = rect.top - ACTION_MENU_HEIGHT - ACTION_MENU_GAP;
    placement = "top";
  }

  const maxTop = Math.max(
    ACTION_MENU_MARGIN,
    viewportHeight - ACTION_MENU_HEIGHT - ACTION_MENU_MARGIN
  );
  top = Math.max(ACTION_MENU_MARGIN, Math.min(top, maxTop));

  return { top, left, placement };
};

const getMemberActionKey = (member) =>
  member?.member_id || `${member?.corp_code || "member"}-${member?.tbl_id || ""}`;

const MemberTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingSearchQuery, setPendingSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("member_id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [memberTypeFilter, setMemberTypeFilter] = useState("ALL");
  const [corpCodeFilters, setCorpCodeFilters] = useState([]);
  const [memberSpecificFilters, setMemberSpecificFilters] = useState({
    ...MEMBER_SPECIFIC_FILTER_DEFAULTS,
  });
  const [pendingMemberSpecificFilters, setPendingMemberSpecificFilters] = useState({
    ...MEMBER_SPECIFIC_FILTER_DEFAULTS,
  });
  const [corpCodes, setCorpCodes] = useState([]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyItemsPerPage, setHistoryItemsPerPage] = useState(10);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyMember, setHistoryMember] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    ...HISTORY_FILTER_DEFAULTS,
  });
  const [historyAppliedFilters, setHistoryAppliedFilters] = useState({
    ...HISTORY_FILTER_DEFAULTS,
  });
  const [historySortBy, setHistorySortBy] = useState("processed_date");
  const [historySortDirection, setHistorySortDirection] = useState("desc");
  const [historyUpdateForOptions, setHistoryUpdateForOptions] = useState([]);
  const [historyProcessedByOptions, setHistoryProcessedByOptions] = useState([]);
  const [historyExportingCsv, setHistoryExportingCsv] = useState(false);
  const [historyExportingExcel, setHistoryExportingExcel] = useState(false);
  const [autoOpenedHistoryMemberId, setAutoOpenedHistoryMemberId] = useState("");
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const activeMembersControllerRef = useRef(null);
  const memberListRequestIdRef = useRef(0);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search]
  );

  useEffect(() => {
    const memberIdParam = String(searchParams.get("member_id") || "").trim();
    if (!memberIdParam) return;

    const applyMemberIdParam = (previousFilters) => {
      if (previousFilters.member_id === memberIdParam) {
        return previousFilters;
      }

      return {
        ...previousFilters,
        member_id: memberIdParam,
      };
    };

    setPendingMemberSpecificFilters(applyMemberIdParam);
    setMemberSpecificFilters((previousFilters) => {
      return applyMemberIdParam(previousFilters);
    });
    setCurrentPage(1);
  }, [searchParams]);

  const statusMap = useMemo(
    () => ({
      0: { name: "Inactive", color: "red" },
      1: { name: "Active", color: "green" },
      2: { name: "Terminated", color: "red" },
      3: { name: "Suspended", color: "yellow" },
      4: { name: "Expired", color: "slate" },
      default: { name: "Unknown", color: "gray" },
    }),
    []
  );

  const normalizeStatusFlag = useCallback((value) => {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }, []);

  const isMemberActiveLike = useCallback(
    (statusFlag) => {
      const normalized = normalizeStatusFlag(statusFlag);
      return [1, 6, 7, 8].includes(normalized);
    },
    [normalizeStatusFlag]
  );

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  }), []);

  const fetchMembers = useCallback(async () => {
    const requestId = memberListRequestIdRef.current + 1;
    memberListRequestIdRef.current = requestId;

    if (activeMembersControllerRef.current) {
      activeMembersControllerRef.current.abort();
    }

    const controller = new AbortController();
    activeMembersControllerRef.current = controller;
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/members`, {
        headers: getAuthHeaders(),
        signal: controller.signal,
        params: {
          limit_offset: (currentPage - 1) * itemsPerPage,
          search_string: searchQuery,
          sort_by: sortBy,
          sort_direction: sortDirection,
          limit: itemsPerPage,
          include_filter_options: 0,
          status_flag: statusFilter === "ALL" ? undefined : statusFilter,
          member_type: memberTypeFilter === "ALL" ? undefined : memberTypeFilter,
          corp_codes: corpCodeFilters.length > 0 ? corpCodeFilters.join(",") : undefined,
          member_id: toOptionalFilterParam(memberSpecificFilters.member_id),
          employee_id: toOptionalFilterParam(memberSpecificFilters.employee_id),
          last_name: toOptionalFilterParam(memberSpecificFilters.last_name),
          first_name: toOptionalFilterParam(memberSpecificFilters.first_name),
          middle_name: toOptionalFilterParam(memberSpecificFilters.middle_name),
        },
      });

      if (requestId !== memberListRequestIdRef.current) {
        return;
      }

      setData(response.data.members || []);
      setTotalCount(response.data.total_count || 0);
      const backendCorporateRows = response.data?.filter_options?.corporates || [];
      if (backendCorporateRows.length > 0) {
        setCorpCodes((previousOptions) =>
          mergeCorporateOptions(previousOptions, backendCorporateRows)
        );
      }
    } catch (error) {
      const wasCanceled =
        axios.isCancel(error) ||
        error?.code === "ERR_CANCELED" ||
        error?.name === "CanceledError";

      if (!wasCanceled && requestId === memberListRequestIdRef.current) {
        toast.error("Failed to fetch members.", { autoClose: 3000 });
      }
    } finally {
      if (requestId === memberListRequestIdRef.current) {
        setLoading(false);
        if (activeMembersControllerRef.current === controller) {
          activeMembersControllerRef.current = null;
        }
      }
    }
  }, [
    getAuthHeaders,
    currentPage,
    itemsPerPage,
    searchQuery,
    sortBy,
    sortDirection,
    statusFilter,
    memberTypeFilter,
    corpCodeFilters,
    memberSpecificFilters,
  ]);

  const fetchCorpCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/corporates`, {
        headers: getAuthHeaders(),
        params: { limit: 1000, sort_by: "corp_code", sort_direction: "asc" },
      });
      const corporateRows = response.data?.corporates || response.data?.data || [];
      const normalizedCorporateRows = (corporateRows || [])
        .map((corporateRow) => {
          const corpCode = String(corporateRow?.corp_code || "").trim();
          if (corpCode === "") {
            return null;
          }

          const corpName = String(corporateRow?.corp_name || "").trim();
          return {
            value: corpCode,
            label: corpName !== "" ? `${corpName} (${corpCode})` : corpCode,
          };
        })
        .filter(Boolean);

      setCorpCodes((previousOptions) =>
        mergeCorporateOptions(previousOptions, normalizedCorporateRows)
      );
    } catch (error) {
      toast.error("Failed to fetch corporate codes.", { autoClose: 3000 });
    }
  }, [getAuthHeaders]);

  const selectedCorpCodeOptions = useMemo(() => {
    if (!Array.isArray(corpCodes) || corpCodes.length === 0) {
      return [];
    }

    const selectedLookup = new Set(corpCodeFilters);
    return corpCodes.filter((corpCodeOption) => selectedLookup.has(corpCodeOption.value));
  }, [corpCodes, corpCodeFilters]);

  const hasSpecificMemberFilters = useMemo(
    () =>
      Object.values(memberSpecificFilters).some(
        (filterValue) => String(filterValue ?? "").trim() !== ""
      ),
    [memberSpecificFilters]
  );

  const hasActiveMemberFilters = useMemo(
    () =>
      Boolean(
        searchQuery ||
          statusFilter !== "ALL" ||
          memberTypeFilter !== "ALL" ||
          corpCodeFilters.length > 0 ||
          hasSpecificMemberFilters
      ),
    [
      searchQuery,
      statusFilter,
      memberTypeFilter,
      corpCodeFilters,
      hasSpecificMemberFilters,
    ]
  );

  const handleApplyMemberFilters = useCallback(() => {
    const nextSearchQuery = normalizeMemberSearchText(pendingSearchQuery);
    const nextSpecificFilters = normalizeMemberSpecificFilters(
      pendingMemberSpecificFilters
    );

    setPendingSearchQuery(nextSearchQuery);
    setPendingMemberSpecificFilters(nextSpecificFilters);
    setSearchQuery(nextSearchQuery);
    setMemberSpecificFilters(nextSpecificFilters);
    setCurrentPage(1);
  }, [pendingMemberSpecificFilters, pendingSearchQuery]);

  const handleMemberSpecificFilterChange = useCallback((fieldKey, fieldValue) => {
    setPendingMemberSpecificFilters((previousFilters) => ({
      ...previousFilters,
      [fieldKey]: fieldValue,
    }));
  }, []);

  const sortableColumnAccessors = useMemo(
    () =>
      new Set([
        "member_id",
        "last_name",
        "first_name",
        "middle_name",
        "corp_code",
        "corp_prefix",
        "actual_corp_name",
        "member_type",
        "plan_name",
        "status_flag",
      ]),
    []
  );

  const handleColumnSort = useCallback(
    (columnId) => {
      if (!sortableColumnAccessors.has(columnId)) {
        return;
      }

      if (sortBy === columnId) {
        setSortDirection((previousDirection) =>
          previousDirection === "asc" ? "desc" : "asc"
        );
      } else {
        setSortBy(columnId);
        setSortDirection("asc");
      }

      setCurrentPage(1);
    },
    [sortBy, sortableColumnAccessors]
  );

  const buildHistoryRequestParams = useCallback(
    ({ forExport = false } = {}) => {
      const effectiveFilters = historyAppliedFilters || HISTORY_FILTER_DEFAULTS;
      const resolvedLimit = forExport ? 10000 : historyItemsPerPage;
      const resolvedOffset = forExport
        ? 0
        : Math.max(0, (historyCurrentPage - 1) * historyItemsPerPage);

      return {
        limit: resolvedLimit,
        limit_offset: resolvedOffset,
        search_string: effectiveFilters.search_string || undefined,
        update_for:
          effectiveFilters.update_for && effectiveFilters.update_for !== "all"
            ? effectiveFilters.update_for
            : undefined,
        processed_by:
          effectiveFilters.processed_by && effectiveFilters.processed_by !== "all"
            ? effectiveFilters.processed_by
            : undefined,
        effective_date_from: effectiveFilters.effective_date_from || undefined,
        effective_date_to: effectiveFilters.effective_date_to || undefined,
        processed_date_from: effectiveFilters.processed_date_from || undefined,
        processed_date_to: effectiveFilters.processed_date_to || undefined,
        sort_by: historySortBy,
        sort_direction: historySortDirection,
        for_export: forExport ? "1" : "0",
      };
    },
    [
      historyAppliedFilters,
      historyCurrentPage,
      historyItemsPerPage,
      historySortBy,
      historySortDirection,
    ]
  );

  const loadMemberMovementHistory = useCallback(
    async (memberId, params, { silent = false } = {}) => {
      if (!memberId) return null;

      if (!silent) {
        setHistoryLoading(true);
      }
      setHistoryError("");

      try {
        const response = await axios.get(`${BASE_URL}/members/${memberId}/history`, {
          headers: getAuthHeaders(),
          params,
        });

        if (!silent) {
          setHistoryRows(response.data?.data || []);
          setHistoryTotalCount(Number(response.data?.total_count || 0));
          setHistoryUpdateForOptions(response.data?.filter_options?.update_for || []);
          setHistoryProcessedByOptions(response.data?.filter_options?.processed_by || []);
        }

        return response.data?.data || [];
      } catch (error) {
        if (!silent) {
          const backendMessage = error.response?.data?.message;
          const message =
            (Array.isArray(backendMessage) ? backendMessage[0] : backendMessage) ||
            "Failed to fetch member movement history.";
          setHistoryRows([]);
          setHistoryTotalCount(0);
          setHistoryError(message);
          toast.error(message, { autoClose: 3000 });
        }
        return null;
      } finally {
        if (!silent) {
          setHistoryLoading(false);
        }
      }
    },
    [getAuthHeaders]
  );

  const fetchHistoryRowsForExport = useCallback(
    async (memberId) => {
      const exportParams = buildHistoryRequestParams({ forExport: true });
      return loadMemberMovementHistory(memberId, exportParams, { silent: true });
    },
    [buildHistoryRequestParams, loadMemberMovementHistory]
  );

  const openMemberHistoryModal = useCallback((member) => {
    const memberId = member?.member_id;
    if (!memberId) return;

    const displayName = `${member.last_name || ""}, ${member.first_name || ""} ${
      member.middle_name || ""
    }`
      .replace(/\s+/g, " ")
      .replace(/^,\s*/, "")
      .trim();

    setHistoryMember({
      member_id: memberId,
      member_name: displayName || memberId,
    });
    setHistoryFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistoryAppliedFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistorySortBy("processed_date");
    setHistorySortDirection("desc");
    setHistoryItemsPerPage(10);
    setHistoryCurrentPage(1);
    setHistoryRows([]);
    setHistoryTotalCount(0);
    setHistoryError("");
    setHistoryUpdateForOptions([]);
    setHistoryProcessedByOptions([]);
    setIsHistoryModalOpen(true);
  }, []);

  const closeMemberHistoryModal = useCallback(() => {
    setIsHistoryModalOpen(false);
    setHistoryRows([]);
    setHistoryTotalCount(0);
    setHistoryLoading(false);
    setHistoryError("");
    setHistoryFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistoryAppliedFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistorySortBy("processed_date");
    setHistorySortDirection("desc");
    setHistoryItemsPerPage(10);
    setHistoryCurrentPage(1);
    setHistoryUpdateForOptions([]);
    setHistoryProcessedByOptions([]);
    setHistoryExportingCsv(false);
    setHistoryExportingExcel(false);
    setHistoryMember(null);
  }, []);

  const handleApplyHistoryFilters = useCallback(() => {
    setHistoryAppliedFilters({ ...historyFilters });
    setHistoryCurrentPage(1);
  }, [historyFilters]);

  const handleResetHistoryFilters = useCallback(() => {
    setHistoryFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistoryAppliedFilters({ ...HISTORY_FILTER_DEFAULTS });
    setHistorySortBy("processed_date");
    setHistorySortDirection("desc");
    setHistoryItemsPerPage(10);
    setHistoryCurrentPage(1);
  }, []);

  const historyTotalPages = useMemo(
    () => Math.max(1, Math.ceil(historyTotalCount / historyItemsPerPage)),
    [historyItemsPerPage, historyTotalCount]
  );

  const handleHistoryPageChange = useCallback(
    (page) => {
      if (page >= 1 && page <= historyTotalPages) {
        setHistoryCurrentPage(page);
      }
    },
    [historyTotalPages]
  );

  const handleExportHistoryCsv = useCallback(async () => {
    if (!historyMember?.member_id) return;

    setHistoryExportingCsv(true);
    try {
      const exportRows = await fetchHistoryRowsForExport(historyMember.member_id);
      if (!exportRows || !exportRows.length) {
        toast.info("No member movement history to export.");
        return;
      }

      const fileName = `member_movement_history_${historyMember.member_id}_${dayjs().format(
        "YYYYMMDD_HHmmss"
      )}.csv`;
      exportMovementHistoryToCsv(exportRows, fileName);
    } catch (error) {
      toast.error(`Failed to export CSV: ${error.message}`);
    } finally {
      setHistoryExportingCsv(false);
    }
  }, [fetchHistoryRowsForExport, historyMember]);

  const handleExportHistoryExcel = useCallback(async () => {
    if (!historyMember?.member_id) return;

    setHistoryExportingExcel(true);
    try {
      const exportRows = await fetchHistoryRowsForExport(historyMember.member_id);
      if (!exportRows || !exportRows.length) {
        toast.info("No member movement history to export.");
        return;
      }

      const fileName = `member_movement_history_${historyMember.member_id}_${dayjs().format(
        "YYYYMMDD_HHmmss"
      )}.xlsx`;
      await exportMovementHistoryToExcel(exportRows, fileName);
    } catch (error) {
      toast.error(`Failed to export Excel: ${error.message}`);
    } finally {
      setHistoryExportingExcel(false);
    }
  }, [fetchHistoryRowsForExport, historyMember]);

  useEffect(() => {
    if (!isHistoryModalOpen || !historyMember?.member_id) {
      return;
    }

    const params = buildHistoryRequestParams();
    loadMemberMovementHistory(historyMember.member_id, params);
  }, [
    buildHistoryRequestParams,
    historyAppliedFilters,
    historyCurrentPage,
    historyItemsPerPage,
    historyMember,
    historySortBy,
    historySortDirection,
    isHistoryModalOpen,
    loadMemberMovementHistory,
  ]);

  useEffect(() => {
    if (historyCurrentPage > historyTotalPages) {
      setHistoryCurrentPage(historyTotalPages);
    }
  }, [historyCurrentPage, historyTotalPages]);

  useEffect(() => {
    return () => {
      if (activeMembersControllerRef.current) {
        activeMembersControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    fetchCorpCodes();
  }, [fetchCorpCodes]);

  useEffect(() => {
    const memberIdParam = String(searchParams.get("member_id") || "").trim();
    const shouldOpenHistory = searchParams.get("open_history") === "1";

    if (
      !shouldOpenHistory ||
      !memberIdParam ||
      loading ||
      isHistoryModalOpen ||
      autoOpenedHistoryMemberId === memberIdParam
    ) {
      return;
    }

    const matchedMember = data.find(
      (member) => String(member.member_id || "") === memberIdParam
    );

    if (matchedMember) {
      setAutoOpenedHistoryMemberId(memberIdParam);
      openMemberHistoryModal(matchedMember);
    }
  }, [
    autoOpenedHistoryMemberId,
    data,
    isHistoryModalOpen,
    loading,
    openMemberHistoryModal,
    searchParams,
  ]);

  const handleResendWelcomeAlert = useCallback(async (
    memberId,
    fullName,
    mobTel,
    emailAdd
  ) => {
    const result = await Swal.fire({
      title: "Resend Member's Text Welcome Alert",
      html: `
      <div class="text-left">
        <p><strong>Member:</strong> ${fullName}</p>
        <p><strong>Mobile#:</strong> ${mobTel || "N/A"}</p>
        <p><strong>Email#:</strong> ${emailAdd || "N/A"}</p>
      </div>
      <p>Are you sure you want to resend welcome text alert?</p>
    `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        confirmButton:
          "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
        cancelButton:
          "bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300",
      },
    });

    if (result.isConfirmed) {
      const modeResult = await Swal.fire({
        title: "Resend Welcome Alert via",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        showDenyButton: true,
        confirmButtonText: "Email",
        denyButtonText: "SMS",
        buttonsStyling: false,
        customClass: {
          confirmButton:
            "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
          denyButton:
            "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
          cancelButton:
            "bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300",
        },
      });

      if (modeResult.isConfirmed || modeResult.isDenied) {
        const mode = modeResult.isConfirmed ? "email" : "sms";
        try {
          const response = await axios.post(
            `${BASE_URL}/members/resend-welcome-alert/${memberId}`,
            { mode },
            { headers: getAuthHeaders() }
          );
          if (response.data.status === "success") {
            toast.success(response.data.message);
          } else {
            toast.error(
              response.data.message || `Failed to send ${mode} alert.`
            );
          }
        } catch (error) {
          toast.error(
            `Failed to send ${mode} alert: ` +
              (error.response?.data?.message || error.message)
          );
        }
      }
    }
  }, [getAuthHeaders]);

  const handleMemberStatusToggle = useCallback(
    async (member) => {
      const currentStatus = normalizeStatusFlag(member?.status_flag);
      const shouldDeactivate = isMemberActiveLike(currentStatus);
      const targetStatusFlag = shouldDeactivate ? 0 : 1;
      const actionLabel = shouldDeactivate ? "Deactivate" : "Activate";
      const actionLabelLower = actionLabel.toLowerCase();
      const memberType = String(member?.member_type || "")
        .trim()
        .toLowerCase();
      const isPrincipalMember = memberType === "p";
      const memberDisplayName = `${member?.last_name || ""}, ${member?.first_name || ""} ${
        member?.middle_name || ""
      }`
        .replace(/\s+/g, " ")
        .replace(/^,\s*/, "")
        .trim();

      const result = await Swal.fire({
        title: `${actionLabel} Member`,
        html: `
          <div class="text-left">
            <p><strong>Member ID:</strong> ${member?.member_id || "N/A"}</p>
            <p><strong>Name:</strong> ${memberDisplayName || "N/A"}</p>
            <p><strong>Current Status:</strong> ${
              statusMap[currentStatus]?.name || member?.status_flag || "Unknown"
            }</p>
            <p><strong>Target Status:</strong> ${
              targetStatusFlag === 1 ? "Active" : "Inactive"
            }</p>
            ${
              isPrincipalMember
                ? `
              <div class="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                <label class="flex items-center gap-2 text-sm text-gray-700">
                  <input id="apply_to_dependents_checkbox" type="checkbox" class="h-4 w-4" />
                  Apply this action to all dependents of this principal
                </label>
              </div>
            `
                : ""
            }
          </div>
          <p>Are you sure you want to ${actionLabelLower} this member?</p>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        buttonsStyling: false,
        customClass: {
          confirmButton:
            "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
          cancelButton:
            "bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300",
        },
        preConfirm: () => {
          const dependentCheckbox = document.getElementById(
            "apply_to_dependents_checkbox"
          );
          return {
            applyToDependents: dependentCheckbox
              ? Boolean(dependentCheckbox.checked)
              : false,
          };
        },
      });

      if (!result.isConfirmed) {
        return;
      }

      const applyToDependents =
        isPrincipalMember && Boolean(result?.value?.applyToDependents);

      if (applyToDependents) {
        const cascadeConfirmResult = await Swal.fire({
          title: `${actionLabel} Dependents`,
          text: `Are you sure? This will ${actionLabelLower} all dependents of this principal.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Proceed",
          cancelButtonText: "Cancel",
          buttonsStyling: false,
          customClass: {
            confirmButton:
              "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mr-2",
            cancelButton:
              "bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300",
          },
        });

        if (!cascadeConfirmResult.isConfirmed) {
          return;
        }
      }

      try {
        const response = await axios.post(
          `${BASE_URL}/members/${member.member_id}/status`,
          {
            type: "activate",
            status_flag: targetStatusFlag,
            apply_to_dependents: applyToDependents ? 1 : 0,
            bank_id: "",
            bank_branch: "",
          },
          { headers: getAuthHeaders() }
        );

        if (response?.data?.status === "success") {
          const successMessageRaw = Array.isArray(response.data.message)
            ? response.data.message[0]
            : response.data.message || `Member ${actionLabelLower}d successfully.`;
          const updatedDependentsCount = Number(
            response?.data?.data?.updated_dependents_count || 0
          );
          const cascadeApplied =
            Number(response?.data?.data?.apply_to_dependents || 0) === 1;
          const successMessage = cascadeApplied
            ? `${successMessageRaw} (Dependents affected: ${updatedDependentsCount})`
            : successMessageRaw;
          toast.success(successMessage);
          await fetchMembers();
          return;
        }

        const backendMessage = response?.data?.message;
        const message =
          (Array.isArray(backendMessage) ? backendMessage[0] : backendMessage) ||
          `Failed to ${actionLabelLower} member.`;
        toast.error(message);
      } catch (error) {
        const backendMessage = error?.response?.data?.message;
        const message =
          (Array.isArray(backendMessage) ? backendMessage[0] : backendMessage) ||
          error.message ||
          `Failed to ${actionLabelLower} member.`;
        toast.error(message);
      }
    },
    [fetchMembers, getAuthHeaders, isMemberActiveLike, normalizeStatusFlag, statusMap]
  );

  const closeActionMenu = useCallback(() => {
    setOpenActionMenu(null);
    setActionMenuPosition(null);
  }, []);

  const handleActionMenuToggle = useCallback(
    (member, event) => {
      event.stopPropagation();
      const rowKey = getMemberActionKey(member);

      if (openActionMenu === rowKey) {
        closeActionMenu();
        return;
      }

      setOpenActionMenu(rowKey);
      setActionMenuPosition(getActionMenuPosition(event.currentTarget));
    },
    [closeActionMenu, openActionMenu]
  );

  const buildMemberTrackerPayload = useCallback((member) => {
    const fullName = `${member?.last_name || ""}, ${member?.first_name || ""} ${
      member?.middle_name || ""
    }`
      .replace(/\s+/g, " ")
      .replace(/^,\s*/, "")
      .trim();

    return {
      member_id: member?.member_id,
      member_name: fullName,
      first_name: member?.first_name,
      middle_name: member?.middle_name,
      last_name: member?.last_name,
      corp_code: member?.corp_code,
      corp_name:
        member?.actual_corp_name ||
        member?.corp_name ||
        member?.corporate_name ||
        "",
      actual_corp_name: member?.actual_corp_name,
      plan_code: member?.plan_code || member?.plan || "",
    };
  }, []);

  const handleUseMemberInTracker = useCallback(
    (member, event) => {
      event?.stopPropagation?.();
      publishTrackerMemberSelection(buildMemberTrackerPayload(member));
      toast.success("Member sent to Tracker.");
    },
    [buildMemberTrackerPayload]
  );

  useEffect(() => {
    if (!openActionMenu) {
      return undefined;
    }

    const handleViewportChange = () => closeActionMenu();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [closeActionMenu, openActionMenu]);

  const columns = useMemo(
    () => [
      {
        Header: <span className="sr-only">Use in Tracker</span>,
        id: "tracker_shortcut",
        disableSortBy: true,
        Cell: ({ row }) => {
          const member = row.original;

          return (
            <button
              type="button"
              aria-label={`Use member ${member?.member_id || ""} in Tracker`}
              title="Use in Tracker"
              onClick={(event) => handleUseMemberInTracker(member, event)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-100 hover:shadow focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-1"
            >
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          );
        },
      },
      {
        Header: "Member ID",
        accessor: "member_id",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Last Name",
        accessor: "last_name",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "First Name",
        accessor: "first_name",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Middle Name",
        accessor: "middle_name",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Corp Code",
        accessor: "corp_code",
        Cell: ({ value }) => value || "N/A",
      },
      // {
      //   Header: "Corporate Name",
      //   accessor: "corp_name",
      //   Cell: ({ value }) => value || "N/A",
      // },
      {
        Header: "Corp Prefix",
        accessor: "corp_prefix",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Actual Corp Name",
        accessor: "actual_corp_name",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "Member Type",
        accessor: "member_type",
        Cell: ({ value }) => {
          const map = {
            p: { label: "P - Principal", color: "blue" },
            P: { label: "P - Principal", color: "blue" },
            d: { label: "D - Dependent", color: "orange" },
            D: { label: "D - Dependent", color: "orange" },
            b: { label: "B - Both", color: "purple" },
            B: { label: "B - Both", color: "purple" },
          };

          const plan = map[value] || { label: "Unknown", color: "gray" };

          return <Badge color={plan.color}>{plan.label}</Badge>;
        },
      },
      // {
      //   Header: "Plan",
      //   accessor: "plan_name",
      //   Cell: ({ value }) => value || "N/A",
      // },
      // {
      //   Header: "Plan",
      //   accessor: "plan_name",
      //   Cell: ({ value }) => {
      //     const plan = planColorMap[value] || { label: value || "Unknown", color: "#696969", gradient: "linear-gradient(45deg, #696969, #A9A9A9)" };

      //     console.log(plan)

      //     return (
      //       <Badge
      //       color={plan.color}
      //         // style={{
      //         //   // background: plan.gradient,
      //         //   background: "linear-gradient(45deg, #FFD700, #FFA500)",
      //         //   color: "#fff",
      //         //   borderRadius: "8px",
      //         //   padding: "4px 8px",
      //         // }}
      //       >
      //         {value}
      //       </Badge>
      //     );
      //   },
      // },

      {
        Header: "Plan",
        accessor: "plan_name",
        Cell: ({ value }) => <PlanBadge value={value} />,
      },
      {
        Header: "Status",
        accessor: "status_flag",
        Cell: ({ value }) => {
          const status = statusMap[value] || statusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => {
          const member = row.original;
          const rowKey = getMemberActionKey(member);
          const fullName = `${member.last_name || ""}, ${member.first_name || ""} ${
            member.middle_name || ""
          }`
            .replace(/\s+/g, " ")
            .replace(/^,\s*/, "")
            .trim();
          const actions = [
            {
              key: "tracker",
              label: "Use in Tracker",
              Icon: ArrowRightIcon,
              color: "text-emerald-600",
              onClick: () => handleUseMemberInTracker(member),
            },
            {
              key: "view",
              label: "View details",
              Icon: EyeIcon,
              color: "text-sky-600",
              onClick: () => {
                setSelectedMemberId(member.member_id);
                setIsViewModalOpen(true);
              },
            },
            {
              key: "edit",
              label: "Edit member",
              Icon: PencilSquareIcon,
              color: "text-blue-600",
              onClick: () => navigate(`/members/edit/${member.member_id}`),
            },
            {
              key: "history",
              label: "Movement history",
              Icon: ClockIcon,
              color: "text-violet-600",
              onClick: () => openMemberHistoryModal(member),
            },
            {
              key: "status",
              label: isMemberActiveLike(member.status_flag)
                ? "Deactivate member"
                : "Activate member",
              Icon: DocumentCheckIcon,
              color: "text-amber-600",
              onClick: () => handleMemberStatusToggle(member),
            },
            {
              key: "resend",
              label: "Resend welcome alert",
              Icon: RocketLaunchIcon,
              color: "text-rose-600",
              onClick: () =>
                handleResendWelcomeAlert(
                  member.member_id,
                  fullName,
                  member.mob_tel,
                  member.email_add
                ),
            },
          ];

          return (
            <div className="relative">
              <button
                type="button"
                aria-label={`Actions for member ${member.member_id || ""}`}
                onClick={(event) => handleActionMenuToggle(member, event)}
                className={clsx(
                  "inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                  openActionMenu === rowKey
                    ? "border-blue-400 bg-blue-50 text-blue-700 shadow-blue-100"
                    : "border-gray-300 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                )}
              >
                <EllipsisVerticalIcon className="h-4 w-4 text-gray-500" />
                <span>Actions</span>
              </button>
              {openActionMenu === rowKey &&
                actionMenuPosition &&
                typeof document !== "undefined" &&
                createPortal(
                  <>
                    <button
                      type="button"
                      aria-label="Close member actions menu"
                      className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                      onClick={closeActionMenu}
                    />
                    <div
                      className="fixed z-[9999] w-72 overflow-visible"
                      style={{
                        top: `${actionMenuPosition.top}px`,
                        left: `${actionMenuPosition.left}px`,
                      }}
                    >
                      <div
                        className={clsx(
                          "absolute right-6 h-3 w-3 rotate-45 border bg-white",
                          actionMenuPosition.placement === "top"
                            ? "-bottom-1.5 border-l-0 border-t-0 border-gray-200"
                            : "-top-1.5 border-b-0 border-r-0 border-gray-200"
                        )}
                      />
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-black/5">
                        <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Member Actions
                          </p>
                        </div>
                        <div className="space-y-1 p-2">
                          {actions.map((action) => (
                            <button
                              key={action.key}
                              type="button"
                              onClick={() => {
                                action.onClick();
                                closeActionMenu();
                              }}
                              className="group flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-left text-sm text-gray-700 transition-all duration-150 hover:translate-x-0.5 hover:border-blue-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-700 hover:shadow-sm"
                            >
                              <span className="flex min-w-0 items-center">
                                <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 transition-all duration-150 group-hover:bg-white group-hover:shadow-sm">
                                  <action.Icon className={clsx("h-4 w-4", action.color)} />
                                </span>
                                <span className="truncate font-medium">
                                  {action.label}
                                </span>
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
            </div>
          );
        },
      },
    ],
    [
      navigate,
      openMemberHistoryModal,
      isMemberActiveLike,
      handleMemberStatusToggle,
      handleResendWelcomeAlert,
      handleUseMemberInTracker,
      handleActionMenuToggle,
      openActionMenu,
      actionMenuPosition,
      closeActionMenu,
    ]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: { pageIndex: 0 },
      },
      usePagination
    );

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const getHistoryPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(
      1,
      historyCurrentPage - Math.floor(maxPagesToShow / 2)
    );
    let endPage = Math.min(historyTotalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="p-6">
      <ToastContainer />
      <div className="mb-6 rounded-2xl border border-slate-200 bg-gradient-to-r from-white via-slate-50 to-blue-50/40 p-4 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <FontAwesomeIcon icon={faUser} className="h-5 w-5 text-blue-600" />
              Member Management
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Quick actions for member enrollment, plan updates, and batch uploads.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/members/add")}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition duration-150 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-200 active:translate-y-0 active:shadow-sm"
          >
            <UserPlusIcon className="h-5 w-5" />
            Add Member
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {MEMBER_BATCH_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.key}
                type="button"
                onClick={() => navigate(action.path)}
                className={clsx(
                  "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold shadow-sm transition duration-150 hover:-translate-y-0.5 hover:shadow focus:outline-none focus:ring-2 active:translate-y-0 active:shadow-sm sm:text-sm",
                  action.buttonClass
                )}
              >
                <Icon className="h-4 w-4" />
                {action.label}
              </button>
            );
          })}
        </div>
      </div>

      <form
        className="mb-4 flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          handleApplyMemberFilters();
        }}
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex w-full flex-col gap-1 sm:w-80">
            <label
              htmlFor="member-list-global-search"
              className="text-xs font-semibold text-gray-600"
            >
              Search
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                id="member-list-global-search"
                type="text"
                value={pendingSearchQuery}
                onChange={(e) => setPendingSearchQuery(e.target.value)}
                className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Search members, corp code, or corp name..."
              />
            </div>
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
            Search
          </button>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="member-list-status-filter"
              className="text-xs font-semibold text-gray-600"
            >
              Status
            </label>
            <select
              id="member-list-status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="ALL">All Statuses</option>
              {Object.entries(statusMap).map(
                ([key, { name }]) =>
                  key !== "default" && (
                    <option key={key} value={key}>
                      {name}
                    </option>
                  )
              )}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="member-list-type-filter"
              className="text-xs font-semibold text-gray-600"
            >
              Member Type
            </label>
            <select
              id="member-list-type-filter"
              value={memberTypeFilter}
              onChange={(e) => {
                setMemberTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="ALL">All Member Types</option>
              <option value="p">Principal</option>
              <option value="d">Dependent</option>
              <option value="b">Both</option>
            </select>
          </div>
          <div className="flex min-w-[280px] flex-col gap-1 sm:min-w-[320px]">
            <label
              htmlFor="member-list-corporate-filter"
              className="text-xs font-semibold text-gray-600"
            >
              Corporate
            </label>
            <Select
              inputId="member-list-corporate-filter"
              isMulti
              closeMenuOnSelect={false}
              classNamePrefix="react-select"
              value={selectedCorpCodeOptions}
              options={corpCodes}
              placeholder="Filter Corporates (Multi-select)"
              onChange={(selectedOptions) => {
                const selectedValues = (selectedOptions || []).map(
                  (selectedOption) => selectedOption.value
                );
                setCorpCodeFilters(selectedValues);
                setCurrentPage(1);
              }}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setPendingSearchQuery("");
              setSearchQuery("");
              setStatusFilter("ALL");
              setMemberTypeFilter("ALL");
              setCorpCodeFilters([]);
              setPendingMemberSpecificFilters({
                ...MEMBER_SPECIFIC_FILTER_DEFAULTS,
              });
              setMemberSpecificFilters({ ...MEMBER_SPECIFIC_FILTER_DEFAULTS });
              setSortBy("member_id");
              setSortDirection("asc");
              setCurrentPage(1);
            }}
            className="inline-flex items-center rounded-md border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
          >
            Reset Filters
          </button>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {MEMBER_SPECIFIC_FILTER_FIELDS.map((field) => (
            <div key={field.key} className="flex flex-col gap-1">
              <label
                htmlFor={`member-list-${field.key}`}
                className="text-xs font-semibold text-gray-600"
              >
                {field.label}
              </label>
              <input
                id={`member-list-${field.key}`}
                type="text"
                value={pendingMemberSpecificFilters[field.key]}
                onChange={(e) =>
                  handleMemberSpecificFilterChange(field.key, e.target.value)
                }
                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder={`Enter ${field.label}`}
                aria-label={field.label}
              />
            </div>
          ))}
        </div>
      </form>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="max-h-[60vh] overflow-auto">
          <table
            {...getTableProps()}
            aria-busy={loading}
            className="min-w-full divide-y divide-gray-200"
          >
            <thead className="sticky top-0 bg-gray-50">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    const columnId = String(column.id || "");
                    const isSortable = sortableColumnAccessors.has(columnId);
                    const isSortedColumn = sortBy === columnId;

                    return (
                      <th
                        {...column.getHeaderProps()}
                        className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                      >
                        {isSortable ? (
                          <button
                            type="button"
                            onClick={() => handleColumnSort(columnId)}
                            className="group inline-flex items-center gap-1 text-left transition-colors hover:text-blue-700"
                          >
                            <span>{column.render("Header")}</span>
                            {isSortedColumn ? (
                              sortDirection === "asc" ? (
                                <ChevronUpIcon className="h-3.5 w-3.5 text-blue-600" />
                              ) : (
                                <ChevronDownIcon className="h-3.5 w-3.5 text-blue-600" />
                              )
                            ) : (
                              <ArrowsUpDownIcon className="h-3.5 w-3.5 text-gray-400 group-hover:text-blue-600" />
                            )}
                          </button>
                        ) : (
                          column.render("Header")
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody
              {...getTableBodyProps()}
              className="divide-y divide-gray-100 bg-white"
            >
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8">
                    <div className="flex flex-col items-center gap-3 text-center">
                      <ArrowPathIcon className="h-6 w-6 animate-spin text-blue-600" />
                      <div className="text-sm font-semibold text-gray-700">
                        Loading members...
                      </div>
                      <div className="w-full max-w-5xl">
                        <Skeleton count={6} height={36} />
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        className="transition-colors hover:bg-gray-50"
                      >
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            className="px-4 py-3 text-sm text-gray-800"
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {data.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} className="px-4 py-6">
                        <EmptyState
                          title={
                            hasActiveMemberFilters
                              ? "No matching members"
                              : "No members"
                          }
                          subtitle={
                            hasActiveMemberFilters
                              ? "Try adjusting your search or filters."
                              : "No members available."
                          }
                        />
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 flex flex-col items-center justify-between gap-3 text-sm text-gray-600 sm:flex-row">
        <div>
          Showing {totalCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{" "}
          {Math.min(currentPage * itemsPerPage, totalCount)} of{" "}
          {totalCount.toLocaleString()} entries
        </div>

        <div className="flex items-center gap-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Show</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-xs text-gray-500">entries</span>
          </div>

          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1 || loading}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === 1
                ? "bg-gray-100 text-gray-400"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" />
            First
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === 1
                ? "bg-gray-100 text-gray-400"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <ChevronLeftIcon className="h-5 w-5" />
            Previous
          </button>
          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={clsx(
                "rounded-md px-2 py-1 text-xs",
                currentPage === page
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === totalPages
                ? "bg-gray-100 text-gray-400"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            Next
            <ChevronRightIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || loading}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === totalPages
                ? "bg-gray-100 text-gray-400"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            Last
            <ChevronDoubleRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Render the modal */}
      {isViewModalOpen && (
        <MemberViewModal
          memberId={selectedMemberId}
          onClose={() => setIsViewModalOpen(false)}
        />
      )}

      <AnimatePresence>
        {isHistoryModalOpen ? (
          <motion.div
            className="fixed inset-0 z-[145] flex items-center justify-center bg-slate-900/60 px-4 py-6 backdrop-blur-[1px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              initial={{ y: 18, opacity: 0, scale: 0.99 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 18, opacity: 0, scale: 0.99 }}
              transition={{ duration: 0.2 }}
            >
              <div className="border-b border-slate-200 bg-gradient-to-r from-blue-50 via-sky-50 to-cyan-50 px-5 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 sm:text-lg">
                      Member Movement History
                    </h3>
                    <p className="text-xs text-slate-600 sm:text-sm">
                      {historyMember?.member_name || "N/A"}{" "}
                      <span className="font-semibold text-slate-700">
                        ({historyMember?.member_id || "N/A"})
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleExportHistoryCsv}
                      disabled={historyLoading || historyExportingCsv || historyExportingExcel}
                      className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {historyExportingCsv ? "Exporting CSV..." : "CSV"}
                    </button>
                    <button
                      type="button"
                      onClick={handleExportHistoryExcel}
                      disabled={historyLoading || historyExportingCsv || historyExportingExcel}
                      className="inline-flex items-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {historyExportingExcel ? "Exporting Excel..." : "Excel"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        loadMemberMovementHistory(
                          historyMember?.member_id,
                          buildHistoryRequestParams()
                        )
                      }
                      disabled={historyLoading || historyExportingCsv || historyExportingExcel}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                    >
                      <ArrowPathIcon
                        className={clsx("h-4 w-4", historyLoading && "animate-spin")}
                      />
                      Refresh
                    </button>
                    <button
                      type="button"
                      onClick={closeMemberHistoryModal}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 sm:text-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                  <div className="xl:col-span-2">
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Search
                    </label>
                    <input
                      type="text"
                      value={historyFilters.search_string}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          search_string: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleApplyHistoryFilters();
                        }
                      }}
                      placeholder="Search Update For, Processed By, Reason, Remarks..."
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Update For
                    </label>
                    <select
                      value={historyFilters.update_for}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          update_for: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="all">All</option>
                      {historyUpdateForOptions.map((option) => (
                        <option key={option} value={option}>
                          {getMovementUpdateForMeta(option).label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Processed By
                    </label>
                    <select
                      value={historyFilters.processed_by}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          processed_by: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="all">All</option>
                      {historyProcessedByOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Display per Page
                    </label>
                    <select
                      value={historyItemsPerPage}
                      onChange={(e) => {
                        setHistoryItemsPerPage(Number(e.target.value));
                        setHistoryCurrentPage(1);
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Effective Date From
                    </label>
                    <input
                      type="date"
                      value={historyFilters.effective_date_from}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          effective_date_from: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Effective Date To
                    </label>
                    <input
                      type="date"
                      value={historyFilters.effective_date_to}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          effective_date_to: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Processed Date From
                    </label>
                    <input
                      type="date"
                      value={historyFilters.processed_date_from}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          processed_date_from: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Processed Date To
                    </label>
                    <input
                      type="date"
                      value={historyFilters.processed_date_to}
                      onChange={(e) =>
                        setHistoryFilters((prev) => ({
                          ...prev,
                          processed_date_to: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Sort By
                    </label>
                    <select
                      value={historySortBy}
                      onChange={(e) => {
                        setHistorySortBy(e.target.value);
                        setHistoryCurrentPage(1);
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="processed_date">Processed Date</option>
                      <option value="effective_date">Effective Date</option>
                      <option value="update_for">Update For</option>
                      <option value="processed_by">Processed By</option>
                      <option value="reason">Reason</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Sort Direction
                    </label>
                    <select
                      value={historySortDirection}
                      onChange={(e) => {
                        setHistorySortDirection(e.target.value);
                        setHistoryCurrentPage(1);
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="desc">Descending</option>
                      <option value="asc">Ascending</option>
                    </select>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleResetHistoryFilters}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 sm:text-sm"
                  >
                    Reset Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyHistoryFilters}
                    className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 sm:text-sm"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-5">
                {historyLoading ? (
                  <Skeleton count={6} height={36} />
                ) : historyError ? (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {historyError}
                  </div>
                ) : historyRows.length === 0 ? (
                  <EmptyState
                    title="No movement history"
                    subtitle="No movement records found for this member."
                  />
                ) : (
                  <div className="overflow-auto rounded-xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Update For
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Effective Date
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Processed By
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Reason
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Remarks
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Processed Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {historyRows.map((row, index) => {
                          const updateFor = getMovementUpdateForMeta(row.update_for);

                          return (
                            <tr key={`${row.member_id || "member"}-${row.processed_date || "date"}-${index}`}>
                              <td className="px-3 py-2 text-slate-700">
                                <Badge color={updateFor.color}>{updateFor.label}</Badge>
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                {formatMovementDate(row.effective_date)}
                              </td>
                              <td className="px-3 py-2 text-slate-700">{row.processed_by || "N/A"}</td>
                              <td className="px-3 py-2 text-slate-700">{row.reason || "N/A"}</td>
                              <td className="max-w-[460px] px-3 py-2 text-slate-700">
                                <span className="block whitespace-pre-wrap break-words">
                                  {row.remarks || "N/A"}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                {formatMovementDate(row.processed_date, true)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 bg-slate-50 px-5 py-3">
                <div className="flex flex-col items-center justify-between gap-3 text-sm text-slate-600 sm:flex-row">
                  <div>
                    Showing{" "}
                    {historyTotalCount > 0
                      ? (historyCurrentPage - 1) * historyItemsPerPage + 1
                      : 0}{" "}
                    to{" "}
                    {Math.min(historyCurrentPage * historyItemsPerPage, historyTotalCount)} of{" "}
                    {historyTotalCount.toLocaleString()} entries
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleHistoryPageChange(1)}
                      disabled={historyCurrentPage === 1 || historyLoading}
                      className={clsx(
                        "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                        historyCurrentPage === 1
                          ? "bg-gray-100 text-gray-400"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      <ChevronDoubleLeftIcon className="h-4 w-4" />
                      First
                    </button>
                    <button
                      type="button"
                      onClick={() => handleHistoryPageChange(historyCurrentPage - 1)}
                      disabled={historyCurrentPage === 1 || historyLoading}
                      className={clsx(
                        "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                        historyCurrentPage === 1
                          ? "bg-gray-100 text-gray-400"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                      Previous
                    </button>
                    {getHistoryPageNumbers().map((page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => handleHistoryPageChange(page)}
                        className={clsx(
                          "rounded-md px-2 py-1 text-xs",
                          historyCurrentPage === page
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        )}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleHistoryPageChange(historyCurrentPage + 1)}
                      disabled={historyCurrentPage === historyTotalPages || historyLoading}
                      className={clsx(
                        "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                        historyCurrentPage === historyTotalPages
                          ? "bg-gray-100 text-gray-400"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      Next
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleHistoryPageChange(historyTotalPages)}
                      disabled={historyCurrentPage === historyTotalPages || historyLoading}
                      className={clsx(
                        "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                        historyCurrentPage === historyTotalPages
                          ? "bg-gray-100 text-gray-400"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      )}
                    >
                      Last
                      <ChevronDoubleRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default MemberTable;
